import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Always show popup on page load
    setTimeout(() => setIsVisible(true), 1000);
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    setIsVisible(false);
    
    // You can add analytics tracking here
    // gtag('consent', 'update', { ... });
  };

  const handleAcceptSelected = () => {
    setIsVisible(false);
    
    // You can add analytics tracking here based on preferences
  };

  const handleDeclineAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(onlyNecessary);
    setIsVisible(false);
  };

  const togglePreference = (type) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const cookieTypes = [
    {
      key: 'necessary',
      title: 'Necessary Cookies',
      description: 'Essential for the website to function properly. These cannot be disabled.',
      required: true
    },
    {
      key: 'analytics',
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website by collecting anonymous information.'
    },
    {
      key: 'marketing',
      title: 'Marketing Cookies',
      description: 'Used to track visitors across websites to display relevant advertisements.'
    },
    {
      key: 'functional',
      title: 'Functional Cookies',
      description: 'Enable enhanced functionality and personalization, such as chat support.'
    }
  ];

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <CookieNotification
        as={motion.div}
        initial={{ opacity: 0, x: 400, y: 20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        exit={{ opacity: 0, x: 400, y: 20 }}
        transition={{ duration: 0.5, type: "spring", damping: 25 }}
      >
          <ModalHeader>
            <HeaderContent>
              <CookieIcon>
                <Cookie size={24} />
              </CookieIcon>
              <div>
                <ModalTitle>Cookie Settings</ModalTitle>
                <ModalSubtitle>Manage your privacy preferences</ModalSubtitle>
              </div>
            </HeaderContent>
            <CloseButton onClick={() => setIsVisible(false)}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalContent>
            <MainMessage>
              We use cookies to enhance your experience, analyze site traffic and deliver personalized content. 
              <PolicyLink href="/cookies" target="_blank">
                 Read our Cookie Policy
              </PolicyLink>
            </MainMessage>

            {showSettings && (
              <SettingsSection
                as={motion.div}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <SettingsTitle>Cookie Preferences</SettingsTitle>
                {cookieTypes.map((type) => (
                  <CookieTypeItem key={type.key}>
                    <CookieTypeHeader>
                      <CookieTypeInfo>
                        <CookieTypeName>{type.title}</CookieTypeName>
                        <CookieTypeDesc>{type.description}</CookieTypeDesc>
                      </CookieTypeInfo>
                      <ToggleSwitch
                        active={preferences[type.key]}
                        disabled={type.required}
                        onClick={() => togglePreference(type.key)}
                      >
                        <ToggleSlider active={preferences[type.key]} disabled={type.required} />
                      </ToggleSwitch>
                    </CookieTypeHeader>
                  </CookieTypeItem>
                ))}
              </SettingsSection>
            )}
          </ModalContent>

          <ModalActions>
            <SecondaryActions>
              <ActionButton
                variant="text"
                onClick={() => setShowSettings(!showSettings)}
              >
                <Settings size={16} />
                {showSettings ? 'Hide Settings' : 'Cookie Settings'}
              </ActionButton>
            </SecondaryActions>
            
            {showSettings && (
              <SecondaryActions>
                <ActionButton variant="secondary" onClick={handleAcceptSelected}>
                  <Check size={16} />
                  Accept Selected
                </ActionButton>
              </SecondaryActions>
            )}
            
            <PrimaryActions>
              <ActionButton variant="outline" onClick={handleDeclineAll}>
                Decline All
              </ActionButton>
              <ActionButton variant="primary" onClick={handleAcceptAll}>
                Accept All Cookies
              </ActionButton>
            </PrimaryActions>
          </ModalActions>
        </CookieNotification>
      </AnimatePresence>
  );
};

const CookieNotification = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(8px);
  border-radius: 14px;
  max-width: 300px;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.5);
  z-index: 9999;
  overflow: hidden;
  
  /* Subtle fade overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(59, 130, 246, 0.02) 0%,
      rgba(139, 92, 246, 0.02) 100%
    );
    pointer-events: none;
    z-index: 1;
  }
  
  /* Content container with higher z-index */
  > * {
    position: relative;
    z-index: 2;
  }
  
  @media (max-width: 768px) {
    bottom: 15px;
    right: 15px;
    left: 15px;
    max-width: none;
    width: auto;
  }
  
  @media (max-width: 480px) {
    bottom: 10px;
    right: 10px;
    left: 10px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 14px 0;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    padding: 12px 12px 0;
    margin-bottom: 8px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const CookieIcon = styled.div`
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  
  svg {
    width: 15px;
    height: 15px;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 2px 0;
`;

const ModalSubtitle = styled.p`
  font-size: 0.75rem;
  color: #64748b;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
  flex-shrink: 0;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #1a1a1a;
  }
`;

const ModalContent = styled.div`
  padding: 0 14px;
  
  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const MainMessage = styled.p`
  font-size: 0.8rem;
  line-height: 1.4;
  color: #374151;
  margin-bottom: 12px;
`;

const PolicyLink = styled.a`
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SettingsSection = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 12px;
  margin-bottom: 12px;
`;

const SettingsTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 10px;
`;

const CookieTypeItem = styled.div`
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CookieTypeHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const CookieTypeInfo = styled.div`
  flex: 1;
`;

const CookieTypeName = styled.h4`
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 3px 0;
`;

const CookieTypeDesc = styled.p`
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.3;
  margin: 0;
`;

const ToggleSwitch = styled.button`
  width: 40px;
  height: 22px;
  border-radius: 11px;
  background: ${props => props.active ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)' : '#e2e8f0'};
  border: none;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  position: relative;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover {
    background: ${props => props.disabled ? '#e2e8f0' : props.active ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : '#cbd5e1'};
  }
`;

const ToggleSlider = styled.div`
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 3px;
  left: ${props => props.active ? '21px' : '3px'};
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ModalActions = styled.div`
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  @media (max-width: 768px) {
    padding: 10px 12px 12px;
  }
`;

const SecondaryActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const PrimaryActions = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${props => {
    switch (props.variant) {
      case 'text': return '5px 8px';
      case 'primary': return '8px 16px';
      default: return '6px 12px';
    }
  }};
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          border: none;
          
          &:hover {
            background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
            transform: translateY(-1px);
          }
        `;
      case 'secondary':
        return `
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          border: 1px solid rgba(59, 130, 246, 0.2);
          
          &:hover {
            background: rgba(59, 130, 246, 0.15);
            border-color: rgba(59, 130, 246, 0.3);
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: #64748b;
          border: 1px solid #e2e8f0;
          
          &:hover {
            background: #f8fafc;
            border-color: #cbd5e1;
            color: #374151;
          }
        `;
      case 'text':
        return `
          background: transparent;
          color: #64748b;
          border: none;
          
          &:hover {
            background: rgba(0, 0, 0, 0.05);
            color: #374151;
          }
        `;
      default:
        return `
          background: #f8fafc;
          color: #374151;
          border: 1px solid #e2e8f0;
          
          &:hover {
            background: #f1f5f9;
            border-color: #cbd5e1;
          }
        `;
    }
  }}
`;

export default CookieConsent;
